// rbac.js — base role matrix helpers (builtin + custom-role evaluation)

import matrix from './rbacMatrix.json';

export const BASE_ROLES = Object.freeze({
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member',
});

export const RBAC_VERSION = matrix.version;
export const RBAC_ROLES = Object.freeze([...matrix.roles]);
export const RBAC_ACTIONS = Object.freeze({ ...matrix.actions });

/** Actions that custom roles may toggle in v1 (must be member-true in the matrix). */
export const CUSTOM_ROLE_TOGGLE_ACTIONS = Object.freeze([
  'types.create',
  'types.fields.create',
]);

export function normalizeRole(role) {
  const value = String(role || '').trim().toLowerCase();
  return RBAC_ROLES.includes(value) ? value : null;
}

export function isBuiltinRole(role) {
  return normalizeRole(role) != null;
}

export function normalizeCustomRoleKey(roleKey) {
  const value = String(roleKey || '').trim();
  if (!value) return null;
  if (isBuiltinRole(value)) return null;
  return value;
}

export function defaultCustomRolePermissions() {
  return {
    'types.create': true,
    'types.fields.create': true,
  };
}

export function normalizeCustomRolePermissions(input) {
  const defaults = defaultCustomRolePermissions();
  const source = input && typeof input === 'object' ? input : {};
  const next = {};
  for (const action of CUSTOM_ROLE_TOGGLE_ACTIONS) {
    if (Object.prototype.hasOwnProperty.call(source, action)) {
      next[action] = source[action] === true;
    } else {
      next[action] = defaults[action] === true;
    }
  }
  return next;
}

/**
 * Resolve effective tier (owner|admin|member) and assignable role key.
 */
export function resolveMemberAccess({
  ownerKey = null,
  userKey = null,
  memberRole = null,
  memberBaseRole = null,
} = {}) {
  if (!userKey) {
    return null;
  }

  if (ownerKey && ownerKey === userKey) {
    return {
      roleKey: BASE_ROLES.OWNER,
      tier: BASE_ROLES.OWNER,
      isCustom: false,
    };
  }

  const builtinRole = normalizeRole(memberRole);
  if (builtinRole) {
    return {
      roleKey: builtinRole,
      tier: builtinRole,
      isCustom: false,
    };
  }

  const customKey = normalizeCustomRoleKey(memberRole);
  if (!customKey) {
    return null;
  }

  return {
    roleKey: customKey,
    tier: BASE_ROLES.MEMBER,
    isCustom: true,
  };
}

export function canRole(role, action) {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) return false;

  const permissions = RBAC_ACTIONS[action];
  if (!permissions) return false;

  return permissions[normalizedRole] === true;
}

/**
 * Evaluate an action for builtin or custom roles.
 * Missing/invalid custom role definitions fall back to built-in member.
 */
export function canAccessAction({
  tier,
  isCustom = false,
  roleDefinition = null,
  roleMissing = false,
  action,
} = {}) {
  const normalizedAction = String(action || '').trim();
  if (!normalizedAction) return false;

  if (!isCustom) {
    return canRole(tier, normalizedAction);
  }

  if (roleMissing || !roleDefinition) {
    return canRole(BASE_ROLES.MEMBER, normalizedAction);
  }

  const definitionBase = normalizeRole(roleDefinition.baseRole) || BASE_ROLES.MEMBER;
  if (definitionBase !== BASE_ROLES.MEMBER || roleDefinition.builtin === true) {
    return canRole(BASE_ROLES.MEMBER, normalizedAction);
  }

  // Ceiling: never exceed member matrix.
  if (!canRole(BASE_ROLES.MEMBER, normalizedAction)) {
    return false;
  }

  const permissions = roleDefinition.permissions && typeof roleDefinition.permissions === 'object'
    ? roleDefinition.permissions
    : {};

  if (Object.prototype.hasOwnProperty.call(permissions, normalizedAction)) {
    return permissions[normalizedAction] === true;
  }

  return true;
}

export function getCurrentUserBaseRole({ base, userKey, memberByUserKey }) {
  if (!base || !userKey) return null;

  const memberRow = memberByUserKey?.[userKey] || null;
  const access = resolveMemberAccess({
    ownerKey: base.ownerKey,
    userKey,
    memberRole: memberRow?.role,
    memberBaseRole: memberRow?.baseRole,
  });
  return access?.roleKey || null;
}

export function canUserInBase({
  base,
  userKey,
  memberByUserKey,
  action,
  roleDefinition = null,
  roleMissing = false,
}) {
  if (!base || !userKey) return false;

  const memberRow = memberByUserKey?.[userKey] || null;
  const access = resolveMemberAccess({
    ownerKey: base.ownerKey,
    userKey,
    memberRole: memberRow?.role,
    memberBaseRole: memberRow?.baseRole,
  });
  if (!access) return false;

  return canAccessAction({
    tier: access.tier,
    isCustom: access.isCustom,
    roleDefinition,
    roleMissing,
    action,
  });
}
